import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import ClipLoader from 'react-spinners/ClipLoader';
import { Button, Card, Empty, Flex, message, Result, Spin } from 'antd';
import { isNil } from 'ramda';
import { SideWithAsset } from 'shared-types';

import AssetsSidebar from 'components/AssetsSidebar/AssetsSidebar';
import PagesGrid from 'components/BookLayout/PagesGrid';
import SideEditorModal from 'components/BookLayout/SideEditorModal';
import Box from 'components/Box/Box';

import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';
import useQueryParams from 'hooks/useQueryParams';

import API from 'services/API';
import { insertBlankPageAt } from 'services/blankPageService';
import { isSupportedUploadFile, uploadAssetFile } from 'services/uploadService';

import { SContainer, SLoaderContainer } from './Uploader.styles';

type PendingAction = { type: 'insert' | 'remove' | 'upload' };

// layout=booklayout: upload-sectie over de volle breedte, daaronder alle pagina's als boek-
// setjes in een grid (zie worker/examples/booklayout view.png). Klik op een pagina opent de
// editor + rechter sidebar in een bijna-fullscreen popup. De productie-layout (layout=book)
// blijft ongewijzigd.
const BookLayout = () => {
  const { t } = useTranslation();
  const { sessionId } = useQueryParams();
  const queryClient = useQueryClient();

  const { products, selectedProductId, selectedSideId, setProducts, setSelectedSideId } =
    useProductsStore((state) => ({
      products: state.products,
      selectedProductId: state.selectedProductId,
      selectedSideId: state.selectedSideId,
      setProducts: state.setProducts,
      setSelectedSideId: state.setSelectedSideId,
    }));

  const { setSessionId, setSession, canDeletePages, expectedSidesCount, isUploadEnabled } =
    useSessionStore((state) => ({
      setSessionId: state.setSessionId,
      setSession: state.setSession,
      expectedSidesCount: state.session?.sidesCount ?? 0,
      // Zelfde optie als de upload-dragger in de toolbar.
      isUploadEnabled: state.session?.options?.ui?.enableUpload !== false,
      // Zelfde sessie-optie als de verwijderknop in de SidesList van de book-layout.
      canDeletePages: state.session?.options?.ui?.sideList?.buttons?.deleteSide !== false,
    }));

  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  // Blanco's die we zelf via de grid hebben ingevoegd — die mogen zonder bevestiging weer weg
  // (net als in BookPreviewModal); alle andere pagina's vragen eerst om bevestiging.
  const [insertedBlankSideIds, setInsertedBlankSideIds] = useState<Set<string>>(new Set());

  const { isLoading: isLoadingProducts, isError: isProductsError } = useQuery(
    'products',
    () => API.getProducts(sessionId),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: (data) => {
        setProducts(data);
      },
    },
  );

  const { isLoading, isError: isSessionError } = useQuery(
    'session',
    () => API.getSession(sessionId),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setSessionId(sessionId);
        setSession(data);
      },
    },
  );

  // Een bestand dat buiten de upload-zone wordt gedropt mag de browser niet laten navigeren
  // (standaard "open bestand"-gedrag), want dan is de sessie weg.
  useEffect(() => {
    const preventDefault = (event: DragEvent): void => event.preventDefault();
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Afgeleid uit `products` (reactief) i.p.v. via de store-getters, zodat websocket preview-
  // updates de grid laten her-renderen.
  const selectedProduct =
    products.find((product) => product.id === selectedProductId) ?? products[0];
  const sides = (selectedProduct?.sides ?? []) as SideWithAsset[];
  const hasPages = sides.some((side) => !isNil(side?.assetId));
  // Het grid toont ook placeholders voor nog niet geüploade posities, dus zolang het product
  // pagina's verwacht is er iets te zien — ook vóór de eerste upload.
  const shouldShowGrid = hasPages || expectedSidesCount > 0;
  const foundSideIndex = sides.findIndex((side) => side.id === selectedSideId);
  const selectedSideIndex = foundSideIndex > -1 ? foundSideIndex : 0;

  if (isLoading || isLoadingProducts) {
    return (
      <SLoaderContainer>
        <Spin size="large" />
      </SLoaderContainer>
    );
  }

  if (isSessionError || isProductsError) {
    return (
      <Result
        status="500"
        title="500"
        subTitle={t('BookPage.GenericError')}
        extra={<Button type="primary">{t('BookPage.SendEmail')}</Button>}
      />
    );
  }

  const handleOpenSide = (sideId: string): void => {
    setSelectedSideId(sideId);
    setIsEditorOpen(true);
  };

  const handleNavigate = (sideIndex: number): void => {
    const target = sides[sideIndex];
    if (target) setSelectedSideId(target.id);
  };

  const handleInsertBlankPageAt = async (targetIndex: number): Promise<void> => {
    if (pendingAction || !selectedProduct) return;
    setPendingAction({ type: 'insert' });
    try {
      const { sideId } = await insertBlankPageAt({
        sessionId,
        productId: selectedProduct.id,
        targetIndex,
        queryClient,
      });
      setInsertedBlankSideIds((prev) => new Set(prev).add(sideId));
      setSelectedSideId(sideId);
    } catch (error) {
      console.error('Insert blank page failed', error);
      message.error(t('BookPreview.InsertBlankPageError'));
    } finally {
      setPendingAction(null);
    }
  };

  // Voor gewone pagina's is de bevestiging al gevraagd in PageGridItem; zelf ingevoegde blanco's
  // gaan direct.
  const handleRemovePage = async (sideId: string): Promise<void> => {
    if (pendingAction) return;
    setPendingAction({ type: 'remove' });
    try {
      await API.deleteSide(sessionId, sideId);
      await queryClient.invalidateQueries('products');
      setInsertedBlankSideIds((prev) => {
        const next = new Set(prev);
        next.delete(sideId);
        return next;
      });
    } catch (error) {
      console.error('Remove page failed', error);
      message.error(t('BookLayout.DeletePageError'));
    } finally {
      setPendingAction(null);
    }
  };

  // Upload vanaf de eerste vrije positie in het grid; zelfde pad als de toolbar-upload.
  const handleUploadFiles = async (files: File[]): Promise<void> => {
    if (pendingAction) return;
    const validFiles = files.filter(isSupportedUploadFile);
    if (validFiles.length === 0) {
      message.error(t('BookPage.ErrorMessages.OnlyPdfOrImages'));
      return;
    }
    setPendingAction({ type: 'upload' });
    try {
      for (const file of validFiles) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await uploadAssetFile({ sessionId, file, productId: selectedProduct?.id });
        } catch (error) {
          console.error('Upload error:', error);
          message.error(t('BookPage.ErrorMessages.UploadFailed', { filename: file.name }));
        }
      }
    } finally {
      setPendingAction(null);
    }
  };

  const isSidesFull = sides.length >= expectedSidesCount;
  const canUploadFromGrid = isUploadEnabled && !isSidesFull;

  return (
    <SContainer>
      <Box>
        {/* Compacte toolbar: upload + progress bar + acties. AssetsSidebar haalt ook de assets op
            (nodig voor de editor en ResultOutput) en luistert naar de websockets. */}
        <AssetsSidebar
          variant="toolbar"
          showInstructions={false}
          showAddBlankPage={true}
          shouldFetchAssets={true}
          autoScrollDependency={false}
        />

        <Card style={{ marginTop: 10, position: 'relative' }}>
          {shouldShowGrid ? (
            <PagesGrid
              sides={sides}
              selectedSideId={selectedSideId}
              insertedBlankSideIds={insertedBlankSideIds}
              disabled={pendingAction !== null}
              onOpenSide={handleOpenSide}
              onInsertBlankPageAt={handleInsertBlankPageAt}
              onRemovePage={canDeletePages ? handleRemovePage : undefined}
              onUploadFiles={canUploadFromGrid ? handleUploadFiles : undefined}
            />
          ) : (
            <Empty description={t('BookPage.NoFilesUploaded')} />
          )}

          {pendingAction && (
            <Flex
              vertical
              align="center"
              justify="center"
              gap={12}
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 5,
              }}
            >
              <ClipLoader color="#35b3ee" size={40} />
              <span>
                {t(
                  pendingAction.type === 'insert'
                    ? 'BookPreview.InsertingBlankPage'
                    : pendingAction.type === 'remove'
                      ? 'BookPreview.RemovingPage'
                      : 'Uploading',
                )}
              </span>
            </Flex>
          )}
        </Card>

        {hasPages && (
          <SideEditorModal
            open={isEditorOpen}
            onClose={() => setIsEditorOpen(false)}
            sides={sides}
            sideIndex={selectedSideIndex}
            onNavigate={handleNavigate}
          />
        )}
      </Box>
    </SContainer>
  );
};

export default BookLayout;
